import React, { useState, useEffect } from 'react';
import { dispatchBag, getAvailableTechnicians } from './inventoryApi';

const COLORS = {
  aquaBlue: '#11a8a4',
  darkGrey: '#3c3b3f',
  white: '#ffffff',
  lightGrey: '#f3f4f6',
  mediumGrey: '#e5e7eb',
  darkText: '#1f2937',
  mediumText: '#6b7280',
  errorRed: '#ef4444',
  warningBg: '#fffbeb',
  warningBorder: '#f59e0b',
};

const TRANSFER_REASONS = [
  'On Leave',
  'Resigned',
  'Absent',
  'Replaced',
  'Emergency',
  'Other',
];

/**
 * TransferInventoryModal
 *
 * Props:
 *   bag        {Object}   – The inventory bag being transferred
 *   onClose    {Function} – Called when modal is dismissed
 *   onSuccess  {Function} – Called with updated bag after successful transfer
 */
const TransferInventoryModal = ({ bag, onClose, onSuccess }) => {
  const [technicians, setTechnicians] = useState([]);
  const [techLoading, setTechLoading] = useState(true);
  const [techError, setTechError] = useState(null);

  const [toTechnicianId, setToTechnicianId] = useState('');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Load available technicians (excluding current bag owner)
  useEffect(() => {
    const load = async () => {
      setTechLoading(true);
      setTechError(null);
      try {
        const data = await getAvailableTechnicians(bag.id);
        setTechnicians(data);
      } catch (err) {
        setTechError(
          err.response?.data?.detail || err.message || 'Failed to load technicians'
        );
      } finally {
        setTechLoading(false);
      }
    };
    load();
  }, [bag.id]);

  const effectiveReason = reason === 'Other' ? customReason.trim() : reason;

  const handleSubmit = async () => {
    // Client-side validation
    if (!toTechnicianId) {
      setSubmitError('Please select the new technician.');
      return;
    }
    if (!reason) {
      setSubmitError('Please select a transfer reason.');
      return;
    }
    if (reason === 'Other' && !customReason.trim()) {
      setSubmitError('Please enter the reason details.');
      return;
    }

    setSubmitError(null);
    setSubmitting(true);

    try {
      const updatedBag = await dispatchBag(bag.id, {
        to_technician: Number(toTechnicianId),
        reason: effectiveReason,
        // transferred_at omitted → backend defaults to now()
      });
      onSuccess(updatedBag);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        (typeof err.response?.data === 'object'
          ? Object.values(err.response.data).flat().join(' ')
          : null) ||
        err.message ||
        'Transfer failed. Please try again.';
      setSubmitError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const currentTechName =
    bag.current_technician_details?.name || `Technician #${bag.current_technician}`;

  return (
    // ── Backdrop ──
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
    >
      {/* ── Modal Panel ── */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: COLORS.white,
          borderRadius: '10px',
          width: '100%',
          maxWidth: '480px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          fontFamily: "'Inter', sans-serif",
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: `1px solid ${COLORS.mediumGrey}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: COLORS.lightGrey,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: COLORS.darkText }}>
              🔄 Transfer Inventory Bag
            </h2>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: COLORS.mediumText }}>
              BAG-{bag.id} · {bag.month}/{bag.year}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: COLORS.mediumText,
              lineHeight: 1,
              padding: '4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

          {/* Warning banner */}
          <div
            style={{
              backgroundColor: COLORS.warningBg,
              border: `1px solid ${COLORS.warningBorder}`,
              borderRadius: '6px',
              padding: '12px 14px',
              fontSize: '13px',
              color: '#92400e',
              lineHeight: '1.5',
            }}
          >
            ⚠️ <strong>Important:</strong> The current technician (<strong>{currentTechName}</strong>) will
            immediately lose access to this bag. All future inventory deductions will
            be attributed to the new technician.
          </div>

          {/* From Technician (read-only) */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COLORS.mediumText, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Current Technician (Transferring From)
            </label>
            <div
              style={{
                padding: '9px 12px',
                backgroundColor: COLORS.lightGrey,
                borderRadius: '6px',
                border: `1px solid ${COLORS.mediumGrey}`,
                fontSize: '14px',
                color: COLORS.darkText,
                fontWeight: '500',
              }}
            >
              {currentTechName}
            </div>
          </div>

          {/* To Technician dropdown */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COLORS.mediumText, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              New Technician <span style={{ color: COLORS.errorRed }}>*</span>
            </label>
            {techLoading ? (
              <div style={{ padding: '10px 12px', backgroundColor: COLORS.lightGrey, borderRadius: '6px', fontSize: '13px', color: COLORS.mediumText }}>
                Loading technicians…
              </div>
            ) : techError ? (
              <div style={{ padding: '10px 12px', backgroundColor: '#fee2e2', borderRadius: '6px', fontSize: '13px', color: COLORS.errorRed }}>
                {techError}
              </div>
            ) : technicians.length === 0 ? (
              <div style={{ padding: '10px 12px', backgroundColor: COLORS.lightGrey, borderRadius: '6px', fontSize: '13px', color: COLORS.mediumText }}>
                No other technicians available.
              </div>
            ) : (
              <select
                value={toTechnicianId}
                onChange={(e) => { setToTechnicianId(e.target.value); setSubmitError(null); }}
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.mediumGrey}`,
                  fontSize: '14px',
                  color: COLORS.darkText,
                  backgroundColor: COLORS.white,
                  cursor: 'pointer',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">— Select technician —</option>
                {technicians.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.email ? ` (${t.email})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Transfer Reason */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COLORS.mediumText, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Reason for Transfer <span style={{ color: COLORS.errorRed }}>*</span>
            </label>
            <select
              value={reason}
              onChange={(e) => { setReason(e.target.value); setCustomReason(''); setSubmitError(null); }}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '6px',
                border: `1px solid ${COLORS.mediumGrey}`,
                fontSize: '14px',
                color: COLORS.darkText,
                backgroundColor: COLORS.white,
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            >
              <option value="">— Select reason —</option>
              {TRANSFER_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>

            {reason === 'Other' && (
              <textarea
                value={customReason}
                onChange={(e) => { setCustomReason(e.target.value); setSubmitError(null); }}
                disabled={submitting}
                placeholder="Describe the reason…"
                rows={3}
                style={{
                  width: '100%',
                  marginTop: '8px',
                  padding: '9px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.mediumGrey}`,
                  fontSize: '14px',
                  color: COLORS.darkText,
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              />
            )}
          </div>

          {/* Items preview */}
          {bag.inventory_items && bag.inventory_items.length > 0 && (
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COLORS.mediumText, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Items Being Transferred ({bag.inventory_items.length})
              </label>
              <div
                style={{
                  maxHeight: '120px',
                  overflowY: 'auto',
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: '6px',
                  backgroundColor: COLORS.lightGrey,
                }}
              >
                {bag.inventory_items.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    style={{
                      padding: '7px 12px',
                      borderBottom: idx < bag.inventory_items.length - 1 ? `1px solid ${COLORS.mediumGrey}` : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '13px',
                    }}
                  >
                    <span style={{ color: COLORS.darkText, fontWeight: '500' }}>
                      {item.inventory_item_details?.item_name || `Item #${item.inventory_item}`}
                    </span>
                    <span style={{ color: COLORS.mediumText }}>
                      Remaining: <strong>{item.quantity_remaining}</strong> {item.inventory_item_details?.unit || ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error message */}
          {submitError && (
            <div
              style={{
                backgroundColor: '#fee2e2',
                border: `1px solid ${COLORS.errorRed}`,
                borderRadius: '6px',
                padding: '10px 14px',
                fontSize: '13px',
                color: '#b91c1c',
              }}
            >
              ❌ {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${COLORS.mediumGrey}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            backgroundColor: COLORS.lightGrey,
          }}
        >
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '9px 20px',
              borderRadius: '6px',
              border: `1px solid ${COLORS.mediumGrey}`,
              backgroundColor: COLORS.white,
              color: COLORS.darkText,
              fontSize: '14px',
              fontWeight: '500',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || techLoading || technicians.length === 0}
            style={{
              padding: '9px 24px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: submitting ? '#9ca3af' : '#dc2626',
              color: COLORS.white,
              fontSize: '14px',
              fontWeight: '600',
              cursor: submitting || techLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {submitting ? '⏳ Transferring…' : '🔄 Confirm Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferInventoryModal;